import { useNavigate } from '@fukuri-admins/hooks/common';
import { useAlertDialog } from '@fukuri-admins/ui/common/AlertDialog';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { downloadFile } from '@fukuri-admins/utils/common';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useGetCustomFieldDefinitions,
  useGetHasUngrantedCredit,
  useGetPartnerAdminsClients,
  useGetPartnerAdminsPartnerFeatureFlags,
  usePutPartnerAdminsClientGrantCredit,
} from '~/hooks';
import type { ClientListPresentation } from '../presentations';
import { useGetPartnerAdminsClientsCsv } from './useGetPartnerAdminsClientsCsv';
import { useGetPublicPartnerBranches } from './useGetPublicPartnerBranches';

type PresentationProps = React.ComponentProps<typeof ClientListPresentation>;

/** 利用企業テーブルの行データ型（ClientListPresentation の rows 要素型から導出） */
type ClientRow = PresentationProps['rows'][number];

type IUseClientList = () => {
  state: {
    /** API から取得した利用企業一覧データ */
    clientsData: ReturnType<typeof useGetPartnerAdminsClients>['data'];
    /** 総ページ数 */
    totalPage: number;
    /** 現在のページ番号（1始まり） */
    currentPage: number;
    /** 総件数 */
    totalCount: number;
    /** 会社名検索フォームの値（filterParams からの draft 値） */
    nameValue: string;
    /** 与信状態検索フォームの値（filterParams からの draft 値） */
    creditGrantedValue: string;
    /**
     * 検索条件が適用されているか（committedFilterParams から判定）
     *
     * true のとき 0 件でも Empty 画面を表示せず検索フォームを表示する（PEN-309 対応）
     */
    isFiltered: boolean;
    /**
     * パートナー機能フラグ API レスポンス（raw データ）
     *
     * `credit_evaluation` の enabled 派生は convertToPresentationProps の責務。
     * hook 内では state にそのまま乗せ、Presentation 用への変換は logics 層で行う。
     */
    featureFlagsData: ReturnType<typeof useGetPartnerAdminsPartnerFeatureFlags>['data'];
    /**
     * カスタムフィールド定義 API レスポンス（raw データ）
     *
     * `custom_field_definitions ?? []` の派生は convertToPresentationProps の責務。
     * hook 内では state にそのまま乗せる。
     */
    customFieldDefinitionsData: ReturnType<typeof useGetCustomFieldDefinitions>['data'];
    /**
     * 未与信件数（useGetHasUngrantedCredit の totalCount から導出済み）
     *
     * creditEvaluationEnabled=false のとき 0。
     */
    ungrantedCreditCount: number;
    /** CSV ダウンロード中フラグ（true のときダウンロードボタンを disabled にして多重クリックを防ぐ） */
    isDownloadingCsv: boolean;
    /**
     * 削除ダイアログの対象利用企業行データ
     *
     * undefined のときダイアログは非表示。定義済みのときダイアログを表示し、5項目を渡す（PEN-715 T07d）
     */
    deleteTargetClient?: ClientRow;
    /**
     * マスタ取引店舗一覧 API レスポンス（raw データ）（PEN-985）
     *
     * `partner_branches.length > 0`（1件以上の取引店舗が存在するか）の派生は
     * convertToPresentationProps の責務。hook 内では state にそのまま乗せる。
     */
    partnerBranchesData: ReturnType<typeof useGetPublicPartnerBranches>['data'];
  };
  actions: {
    /** ページ変更時のハンドラ */
    handlePageChange: PresentationProps['pagerProps']['onPageChange'];
    /** 行の「編集」押下時のハンドラ */
    handleEditClient: PresentationProps['onEditClient'];
    /** 行の「削除」押下時のハンドラ（削除ダイアログを開き対象 row を保持する） */
    handleDeleteClient: PresentationProps['onDeleteClient'];
    /** 利用企業削除ダイアログを閉じるハンドラ */
    handleCloseDeleteClientDialog: () => void;
    /**
     * 利用企業削除成功後ハンドラ
     *
     * - 利用企業一覧を再フェッチする
     * - creditEvaluationEnabled=true のとき未与信カウントを再取得する（PEN-699 T08）
     */
    handleDeleteClientSuccess: () => void;
    /** 行クリック時のハンドラ */
    handleRowClick: PresentationProps['onRowClick'];
    /** 会社名フォーム変更時のハンドラ */
    handleChangeName: PresentationProps['onChangeName'];
    /** 与信状態セレクト変更時のハンドラ */
    handleChangeCreditGranted: PresentationProps['onChangeCreditGranted'];
    /** 検索ボタン押下時のハンドラ */
    handleSearch: PresentationProps['onSearch'];
    /** 条件リセットボタン押下時のハンドラ */
    handleReset: PresentationProps['onReset'];
    /** CSV ダウンロードボタン押下時のハンドラ */
    handleDownloadCsv: PresentationProps['onDownloadCsv'];
    /** 与信確認ダイアログを開くハンドラ */
    handleGrantCredit: PresentationProps['onGrantCredit'];
  };
};

/**
 * 利用企業一覧の状態管理とアクションを提供するコンポーネントフック
 *
 * - URL クエリパラメータ（name / credit_granted / page）で検索・ページング状態を管理する
 * - filterParams（useSearchParams）: 入力途中の draft 値。replace:true で URL に書くが API は発火しない
 * - committedFilterParams（useState）: 検索ボタン押下で確定した値。SWR キーとして API を発火する
 * - credit_granted セレクトは creditEvaluationEnabled フラグが true のときのみ表示する
 * - enabled=1 は常時送信する（旧実装と同等）
 * - カスタムフィールド定義（target: 'c'）を取得してテーブル列の動的挿入に使用する
 * - creditEvaluationEnabled=true のとき未与信件数を取得して警告アラートを表示する
 * - 与信確認は useAlertDialog 経由で AlertDialog を表示し、確認後に grantCredit API を発火する
 * - マスタ取引店舗一覧（GET /api/v1/{threeLetterCode}/public/partner_branches）を取得し、
 *   raw データ（partnerBranchesData）をそのまま state に含める（PEN-985）。
 *   1件以上あるか（hasPartnerBranch）の派生は convertToPresentationProps の責務とする
 * - 利用企業削除ダイアログの開閉状態を isDeleteClientDialogOpen で管理する（PEN-566 T07a）
 */
export const useClientList: IUseClientList = () => {
  const [filterParams, setFilterParams] = useSearchParams();
  const navigate = useNavigate();
  const { showAlertDialog } = useAlertDialog();
  const { showToast } = useToast();
  const threeLetterCode = getThreeLetterCode() ?? '';

  // committedFilterParams（useState）: useGetPartnerAdminsClients に渡す API 用パラメータ。
  // 検索ボタン押下・クリア・ページ変更時のみ更新することで「検索ボタン押下時のみ API 発火」を実現する。
  const [committedFilterParams, setCommittedFilterParams] = useState(
    () => new URLSearchParams(filterParams)
  );

  // 利用企業削除ダイアログの対象行データ（PEN-566 T07a / PEN-715 T07d）
  // undefined のときダイアログは非表示。非 undefined のときダイアログを表示し、5項目を DeleteClientDialog に渡す
  const [deleteTargetClient, setDeleteTargetClient] = useState<ClientRow | undefined>(undefined);

  // URL クエリパラメータから検索フォームの draft 値を導出する（filterParams から読む）
  const nameValue = filterParams.get('name') || '';
  const creditGrantedValue = filterParams.get('credit_granted') || '';

  // credit_granted が committedFilterParams にあれば '0' | '1' として扱う
  const committedCreditGranted = committedFilterParams.get('credit_granted');
  const creditGrantedParam =
    committedCreditGranted === '0' || committedCreditGranted === '1'
      ? committedCreditGranted
      : undefined;

  const {
    data: clientsData,
    totalPage,
    totalCount,
    mutate: mutateClients,
  } = useGetPartnerAdminsClients({
    threeLetterCode,
    filter: {
      name: committedFilterParams.get('name') || undefined,
      creditGranted: creditGrantedParam,
      enabled: '1',
    },
    page: committedFilterParams.get('page') ? Number(committedFilterParams.get('page')) : undefined,
  });

  // isFiltered: committedFilterParams から判定する（filterParams の draft では判定しない）
  const isFiltered = Boolean(
    committedFilterParams.get('name') || committedFilterParams.get('credit_granted')
  );

  const currentPage = Number(committedFilterParams.get('page')) || 1;

  // パートナー機能フラグを取得する（raw データを state に乗せ、Presentation 用派生は converter で行う）
  const { data: featureFlagsData } = useGetPartnerAdminsPartnerFeatureFlags({ threeLetterCode });

  // hook 内で `useGetHasUngrantedCredit` の引数判定に使うためローカル派生する（converter とは別。state には乗せない）
  const creditEvaluationEnabled =
    featureFlagsData.partner_feature_flags.find(f => f.feature_key === 'credit_evaluation')
      ?.enabled ?? false;

  // カスタムフィールド定義を取得する（target: 'c' = Client モデル）
  // ?? [] 派生は converter の責務のため、raw データをそのまま state に乗せる
  const { data: customFieldDefinitionsData } = useGetCustomFieldDefinitions({
    threeLetterCode,
    target: 'c',
  });

  // 未与信件数を取得する（creditEvaluationEnabled=false のとき fetch をスキップする）
  // ヘッダー解釈は hook 内で完結しており、totalCount が直接返される
  const { totalCount: ungrantedCreditCount, mutate: mutateUngrantedCredit } =
    useGetHasUngrantedCredit({
      threeLetterCode,
      enabled: creditEvaluationEnabled,
    });

  // マスタ取引店舗一覧を取得する（PEN-985）。raw データを state にそのまま乗せ、
  // 1件以上存在するか（hasPartnerBranch）の派生は convertToPresentationProps の責務とする
  const { data: partnerBranchesData } = useGetPublicPartnerBranches({ threeLetterCode });

  // CSV ダウンロード hook（旧実装 getClientsCSV と同様、クエリパラメータなしで全件取得する。検索条件の CSV 反映は LEA-3373 で対応）
  const { trigger: downloadClientsCsv, isMutating: isDownloadingCsv } =
    useGetPartnerAdminsClientsCsv(
      { threeLetterCode },
      {
        onSuccess: blob => downloadFile(blob, 'clients.csv'),
        onError: () => {
          showToast({ message: 'CSVのダウンロードに失敗しました。', status: 'danger' });
        },
      }
    );

  // 与信付与 API hook
  const { trigger: grantCredit } = usePutPartnerAdminsClientGrantCredit(threeLetterCode, {
    onSuccess: () => {
      showToast({ message: '与信を完了しました。', status: 'success' });
      // クライアント一覧を再取得して is_credit_granted の表示を最新状態に更新する
      mutateClients();
      // 未与信件数を再取得して最新状態に更新する
      mutateUngrantedCredit();
    },
    onError: () => {
      showToast({ message: '与信の完了に失敗しました。', status: 'danger' });
    },
  });

  /**
   * 会社名フォーム変更時のハンドラ
   *
   * URL の draft パラメータを replace:true で更新する（API は発火させない）
   *
   * @param value 入力値
   */
  const handleChangeName: PresentationProps['onChangeName'] = (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set('name', value);
    } else {
      newParams.delete('name');
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * 与信状態セレクト変更時のハンドラ
   *
   * URL の draft パラメータを replace:true で更新する（API は発火させない）
   *
   * @param value 選択値（'0': 未完了, '1': 完了, '': 選択なし）
   */
  const handleChangeCreditGranted: PresentationProps['onChangeCreditGranted'] = (value: string) => {
    const newParams = new URLSearchParams(filterParams);
    if (value) {
      newParams.set('credit_granted', value);
    } else {
      newParams.delete('credit_granted');
    }
    setFilterParams(newParams, { replace: true });
  };

  /**
   * 検索ボタン押下時のハンドラ
   *
   * filterParams を committedFilterParams にコピーして API を発火させる
   * page はリセットする（PEN-309 対応: ページ持ち越しバグ防止）
   */
  const handleSearch: PresentationProps['onSearch'] = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * 条件リセットボタン押下時のハンドラ
   *
   * 検索条件（name / credit_granted）と page をすべて削除し、API を再発火する
   */
  const handleReset: PresentationProps['onReset'] = () => {
    const newParams = new URLSearchParams(filterParams);
    newParams.delete('name');
    newParams.delete('credit_granted');
    newParams.delete('page');
    setFilterParams(newParams, { replace: true });
    setCommittedFilterParams(newParams);
  };

  /**
   * ページ変更時のハンドラ
   *
   * committedFilterParams ベースで page を更新して API を発火させる
   *
   * @param page 遷移先のページ番号（1始まり）
   */
  const handlePageChange: PresentationProps['pagerProps']['onPageChange'] = (page: number) => {
    const newCommitted = new URLSearchParams(committedFilterParams);
    newCommitted.set('page', String(page));
    const newParams = new URLSearchParams(filterParams);
    newParams.set('page', String(page));
    setFilterParams(newParams);
    setCommittedFilterParams(newCommitted);
  };

  /**
   * 行の「編集」押下時のハンドラ
   *
   * 利用企業編集ページへ遷移する
   *
   * @param clientId 操作対象の利用企業 ID
   */
  const handleEditClient: PresentationProps['onEditClient'] = clientId => {
    navigate(`/clients/${clientId}/edit`);
  };

  /**
   * 行の「削除」押下時のハンドラ
   *
   * 利用企業削除ダイアログを開き、対象 row を deleteTargetClient に保持する（PEN-715 T07d）
   *
   * NOTE: T08（PEN-699）で DELETE API 連携を実装する。削除成功時に
   * mutateUngrantedCredit() を呼んで未与信カウントを再取得する責務がある（旧仕様: 未与信カウント -1）
   *
   * @param row 操作対象の利用企業行データ（削除ダイアログに5項目を渡すため row 全体を受け取る）
   */
  const handleDeleteClient: PresentationProps['onDeleteClient'] = (row: ClientRow) => {
    setDeleteTargetClient(row);
  };

  /**
   * 利用企業削除ダイアログを閉じるハンドラ
   */
  const handleCloseDeleteClientDialog = () => {
    setDeleteTargetClient(undefined);
  };

  /**
   * 利用企業削除成功後ハンドラ（PEN-699 T08）
   *
   * - 利用企業一覧を再フェッチして削除済み企業が一覧から消えた状態にする
   * - creditEvaluationEnabled=true のとき未与信カウントを再取得する（旧仕様の setUngrantedCreditCount(prev => prev - 1) と等価）
   */
  const handleDeleteClientSuccess = () => {
    mutateClients();
    if (creditEvaluationEnabled) {
      mutateUngrantedCredit();
    }
  };

  /**
   * 行クリック時のハンドラ
   *
   * 利用企業詳細画面へ遷移する
   *
   * @param clientId 操作対象の利用企業 ID
   */
  const handleRowClick: PresentationProps['onRowClick'] = clientId => {
    navigate(`/clients/${clientId}`);
  };

  /**
   * CSV ダウンロードボタン押下時のハンドラ
   *
   * 検索条件に関わらず全件を CSV ダウンロードする（検索条件の CSV 反映は LEA-3373 で対応）
   */
  const handleDownloadCsv: PresentationProps['onDownloadCsv'] = () => {
    downloadClientsCsv();
  };

  /**
   * 与信確認ダイアログを表示するハンドラ
   *
   * AlertDialog で確認後に grantCredit API を発火する
   *
   * @param clientId 操作対象の利用企業 ID
   */
  const handleGrantCredit: PresentationProps['onGrantCredit'] = (clientId: number) => {
    showAlertDialog({
      title: '与信を完了しますか？',
      message: 'この利用企業の与信を完了にします。よろしいですか？',
      confirmText: '確認',
      onConfirm: () => {
        grantCredit(clientId);
      },
    });
  };

  return {
    state: {
      clientsData,
      totalPage,
      currentPage,
      totalCount,
      nameValue,
      creditGrantedValue,
      isFiltered,
      featureFlagsData,
      customFieldDefinitionsData,
      ungrantedCreditCount,
      isDownloadingCsv,
      deleteTargetClient,
      partnerBranchesData,
    },
    actions: {
      handlePageChange,
      handleEditClient,
      handleDeleteClient,
      handleCloseDeleteClientDialog,
      handleDeleteClientSuccess,
      handleRowClick,
      handleChangeName,
      handleChangeCreditGranted,
      handleSearch,
      handleReset,
      handleDownloadCsv,
      handleGrantCredit,
    },
  };
};
